import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

export interface PackageItem {
  key: string;
  entityType: 'market' | 'neighborhood' | 'building';
  entityId: number;
  entityName: string;
  chartName: string;
  chartTitle: string;
  propertyType: string | null;
  buildingCategoryId: number | null;
  yearStart: number | null;
  yearEnd: number | null;
  subareaId: number | null;
  subareaName: string | null;
  sortOrder: string | null;
  hiddenSeries?: string[];
}

interface ContextValue {
  items: PackageItem[];
  panelOpen: boolean;
  myLensesPanelOpen: boolean;
  pulseKey: number;
  activeLensId: number | null;
  activeLensName: string | null;
  addItem: (item: Omit<PackageItem, 'key'>) => void;
  removeItem: (key: string) => void;
  reorderItems: (fromIndex: number, toIndex: number) => void;
  clearItems: () => void;
  setPanelOpen: (open: boolean) => void;
  setMyLensesPanelOpen: (open: boolean) => void;
  setActiveLens: (id: number | null, name: string | null) => void;
  setItemsFromLoad: (items: PackageItem[], lensId: number | null, lensName: string | null) => void;
}

const AnalyticsPackageContext = createContext<ContextValue>({
  items: [],
  panelOpen: false,
  myLensesPanelOpen: false,
  pulseKey: 0,
  activeLensId: null,
  activeLensName: null,
  addItem: () => {},
  removeItem: () => {},
  reorderItems: () => {},
  clearItems: () => {},
  setPanelOpen: () => {},
  setMyLensesPanelOpen: () => {},
  setActiveLens: () => {},
  setItemsFromLoad: () => {},
});

const STORAGE_KEY = 'prism_analytics_package';
const LENS_STATE_KEY = 'prism_active_lens';

export function AnalyticsPackageProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<PackageItem[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [panelOpen, setPanelOpen] = useState(false);
  const [myLensesPanelOpen, setMyLensesPanelOpen] = useState(false);
  const [pulseKey, setPulseKey] = useState(0);
  const [activeLensId, setActiveLensId] = useState<number | null>(() => {
    try {
      const raw = localStorage.getItem(LENS_STATE_KEY);
      return raw ? JSON.parse(raw).id : null;
    } catch {
      return null;
    }
  });
  const [activeLensName, setActiveLensName] = useState<string | null>(() => {
    try {
      const raw = localStorage.getItem(LENS_STATE_KEY);
      return raw ? JSON.parse(raw).name : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    localStorage.setItem(LENS_STATE_KEY, JSON.stringify({ id: activeLensId, name: activeLensName }));
  }, [activeLensId, activeLensName]);

  const addItem = useCallback((item: Omit<PackageItem, 'key'>) => {
    const key = [
      item.entityType, item.entityId, item.chartName,
      item.propertyType ?? '', item.buildingCategoryId ?? '',
      item.yearStart ?? '', item.yearEnd ?? '',
      item.subareaId ?? '',
      item.sortOrder ?? '',
    ].join('|');
    setItems(prev => {
      if (prev.some(i => i.key === key)) return prev;
      return [...prev, { ...item, key }];
    });
    setPulseKey(k => k + 1);
  }, []);

  const removeItem = useCallback((key: string) => {
    setItems(prev => prev.filter(i => i.key !== key));
  }, []);

  const reorderItems = useCallback((fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    setItems(prev => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  }, []);

  const clearItems = useCallback(() => {
    setItems([]);
    setActiveLensId(null);
    setActiveLensName(null);
  }, []);

  const setActiveLens = useCallback((id: number | null, name: string | null) => {
    setActiveLensId(id);
    setActiveLensName(name);
  }, []);

  const setItemsFromLoad = useCallback((newItems: PackageItem[], lensId: number | null, lensName: string | null) => {
    setItems(newItems);
    setActiveLensId(lensId);
    setActiveLensName(lensName);
  }, []);

  return (
    <AnalyticsPackageContext.Provider value={{
      items, panelOpen, myLensesPanelOpen, pulseKey,
      activeLensId, activeLensName,
      addItem, removeItem, reorderItems, clearItems,
      setPanelOpen, setMyLensesPanelOpen, setActiveLens, setItemsFromLoad,
    }}>
      {children}
    </AnalyticsPackageContext.Provider>
  );
}

export function useAnalyticsPackage() {
  return useContext(AnalyticsPackageContext);
}
